import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Shield, Users, UserPlus, Trash2, Check, X, Save, Loader2, ChevronDown, ChevronUp, Mail, KeyRound, ShieldCheck, ShieldOff } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { AdminGate } from "@/components/admin/AdminGate";
import { getMyAccess, inviteMember, listMembers, removeMember, setMemberMaster, setMemberPages, setMemberStatus } from "@/lib/admin/access.functions";
import { ADMIN_PAGE_GROUPS, getAdminPagesByGroup, type AdminPage, type AdminPageGroup } from "@/lib/admin/pages";

export const Route = createFileRoute("/admin/acessos")({
  head: () => ({
    meta: [
      { title: "Controle de Acessos — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminAcessosPage,
});

const STATUS_STYLES = {
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  pending: "bg-amber-500/10 text-admin-accent border-amber-500/20",
  blocked: "bg-red-500/10 text-red-400 border-red-500/20",
};

/** Categorias fixas para organizar a lista de membros. */
const MEMBER_CATEGORIES = [
  { key: "PRINCIPAL", label: "PRINCIPAL", names: ["ADMIN - FEO"] },
  { key: "MASTER", label: "MASTER", names: ["ACESSO - RUPEL", "ACESSO - LUCÃO", "ACESSO - TATÃO", "ACESSO - RAFA"] },
  {
    key: "EQUIPE - MKT",
    label: "EQUIPE - MKT",
    names: ["ACESSO - JOICE", "ACESSO - KAREN", "ACESSO - GABE", "ACESSO - DIANA", "ACESSO - FLAVIO"],
  },
  { key: "EQUIPE - FEO", label: "EQUIPE - FEO", names: ["ACESSO - FUKU", "ACESSO - HELIO", "ACESSO - MARCUS"] },
  { key: "OUTROS", label: "OUTROS", names: ["ACESSO - MZ"] },
] as const;

const norm = (v: string) =>
  (v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();

const DISPLAY_NAMES: Record<string, string> = {
  GABE: "ACESSO - GABE",
  "DIANA HOELTGEBAUM ZAVA": "ACESSO - DIANA",
  "KAREN PEREIRA": "ACESSO - KAREN",
};

const displayMemberName = (name: string | null | undefined) =>
  DISPLAY_NAMES[norm(name ?? "")] ?? name;

function categorizeMembers(members: any[]) {
  const used = new Set<string>();
  const sections = MEMBER_CATEGORIES.map((cat) => {
    const items = cat.names.map((name) => {
      const found = members.find(
        (m) => !used.has(m.id) && norm(displayMemberName(m.nome)) === norm(name),
      );
      if (found) {
        used.add(found.id);
        return found;
      }
      return { id: `slot:${name}`, nome: name, placeholder: true };
    });
    return { label: cat.label, members: items };
  });

  const rest = members.filter((m) => !used.has(m.id));
  if (rest.length) {
    const outros = sections.find((s) => s.label === "OUTROS")!;
    outros.members = [...outros.members, ...rest];
  }

  return sections.filter((s) => s.members.length > 0);
}


function AdminAcessosPage() {
  return (
    <AdminGate route="/admin/acessos">
      <AcessosContent />
    </AdminGate>
  );
}

function AcessosContent() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMaster, setIsMaster] = useState(false);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [masterSaving, setMasterSaving] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNome, setInviteNome] = useState("");
  const [invitePages, setInvitePages] = useState<string[]>([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [pendingPages, setPendingPages] = useState<Record<string, string[]>>({});
  const [savingPages, setSavingPages] = useState<Record<string, boolean>>({});
  const [removing, setRemoving] = useState<string | null>(null);

  const grouped = useMemo(() => getAdminPagesByGroup(), []);
  const sections = useMemo(() => categorizeMembers(members), [members]);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [myAccess, membersData] = await Promise.all([getMyAccess(), listMembers()]);
      setIsMaster(myAccess.isMaster);
      setMyUserId(myAccess.userId);
      setMembers(membersData.members);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar acessos");
    } finally {
      setLoading(false);
    }
  }

  const handleStatus = async (memberId: string, status: "active" | "pending" | "blocked") => {
    try {
      await setMemberStatus({ data: { memberId, status } });
      toast.success("Status atualizado");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao atualizar status");
    }
  };

  const handleMaster = async (memberId: string, next: boolean) => {
    if (!confirm(next ? "Tornar este usuário MASTER (acesso total ao admin)?" : "Remover o acesso MASTER deste usuário?")) return;
    setMasterSaving(memberId);
    try {
      await setMemberMaster({ data: { memberId, isMaster: next } });
      toast.success(next ? "Usuário agora é MASTER" : "Acesso MASTER removido");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao atualizar MASTER");
    } finally {
      setMasterSaving(null);
    }
  };

  const handleSavePages = async (memberId: string) => {
    setSavingPages((s) => ({ ...s, [memberId]: true }));
    try {
      await setMemberPages({ data: { memberId, pages: pendingPages[memberId] ?? [] } });
      toast.success("Permissões salvas");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar permissões");
    } finally {
      setSavingPages((s) => ({ ...s, [memberId]: false }));
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!confirm("Tem certeza que deseja remover este acesso?")) return;
    setRemoving(memberId);
    try {
      await removeMember({ data: { memberId } });
      toast.success("Acesso removido");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover");
    } finally {
      setRemoving(null);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      const result = await inviteMember({
        data: { email: inviteEmail.trim(), nome: inviteNome.trim(), pages: invitePages },
      });
      toast.success(result.message);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteNome("");
      setInvitePages([]);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao convidar");
    } finally {
      setInviteLoading(false);
    }
  };

  const togglePage = (memberId: string, route: string) => {
    setPendingPages((prev) => {
      const current = prev[memberId] ?? members.find((m) => m.id === memberId)?.pages ?? [];
      const next = current.includes(route) ? current.filter((r) => r !== route) : [...current, route];
      return { ...prev, [memberId]: next };
    });
  };

  const toggleGroup = (memberId: string, group: AdminPageGroup, routes: AdminPage[]) => {
    setPendingPages((prev) => {
      const current = prev[memberId] ?? members.find((m) => m.id === memberId)?.pages ?? [];
      const groupRoutes = routes.map((r) => r.route);
      const allSelected = groupRoutes.every((r) => current.includes(r));
      const next = allSelected ? current.filter((r) => !groupRoutes.includes(r)) : Array.from(new Set([...current, ...groupRoutes]));
      return { ...prev, [memberId]: next };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0a1f] text-white/60">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Carregando...
      </div>
    );
  }

  if (!isMaster) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0a1f] text-white">
        <div className="text-center">
          <h1 className="text-xl font-bold mb-2">Acesso restrito</h1>
          <p className="text-white/60">FALAR COM RUPEL PARA GARANTIR SEU ACESSO</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#f5b022]/10 border border-[#f5b022]/20 flex items-center justify-center">
              <Shield className="h-6 w-6 text-admin-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Controle de Acessos</h1>
              <p className="text-sm text-white/60">Gerencie quem acessa cada área do admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate({ to: "/admin" })} className="border-white/10 bg-white/5 text-white hover:bg-white/10">
              Voltar
            </Button>
            <Button onClick={() => setInviteOpen(true)} className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a]">
              <UserPlus className="h-4 w-4 mr-2" /> Convidar
            </Button>
          </div>
        </div>

        <div className="space-y-10">
          {sections.map((section) => (
            <div key={section.label}>
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-admin-accent">{section.label}</h2>
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-xs text-white/40">{section.members.length}</span>
              </div>
              <div className="grid gap-4">
                {section.members.map((m) => {
            if (m.placeholder) {
              return (
                <Card key={m.id} className="border-dashed border-white/15 bg-white/[0.02] text-white">
                  <CardHeader className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full border border-dashed border-white/20 bg-white/5 flex items-center justify-center text-white/40 font-bold">
                          {m.nome?.[0]?.toUpperCase() ?? "?"}
                        </div>
                        <div>
                          <CardTitle className="text-base text-white/70">{m.nome}</CardTitle>
                          <p className="text-sm text-white/40 mt-0.5">Sem conta criada</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={`${STATUS_STYLES.pending} border`}>pendente</Badge>
                        <Button
                          size="sm"
                          onClick={() => {
                            setInviteNome(m.nome);
                            setInviteEmail("");
                            setInvitePages([]);
                            setInviteOpen(true);
                          }}
                          className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] h-8"
                        >
                          <Mail className="h-3.5 w-3.5 mr-1" /> Convidar
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            }
            const isExpanded = expanded[m.id] ?? false;
            const pages = pendingPages[m.id] ?? m.pages ?? [];
            return (
              <Card key={m.id} className="border-white/10 bg-white/5 text-white">
                <CardHeader className="pb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">

                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#f5b022]/20 to-[#f5b022]/5 flex items-center justify-center text-admin-accent font-bold">
                        {m.nome?.[0]?.toUpperCase() ?? "?"}
                      </div>
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <span className="whitespace-pre-line">{displayMemberName(m.nome)}</span>
                          {m.is_master && <Badge className="bg-[#f5b022]/10 text-admin-accent border-[#f5b022]/20">MASTER</Badge>}
                        </CardTitle>
                        <p className="text-sm text-white/50 flex items-center gap-1 mt-0.5">
                          <Mail className="h-3 w-3" /> {m.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`${STATUS_STYLES[m.status as keyof typeof STATUS_STYLES]} capitalize border`}>{m.status}</Badge>
                      {!m.is_master && (
                        <>
                          {m.status !== "active" && (
                            <Button size="sm" variant="outline" onClick={() => handleStatus(m.id, "active")} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-8">
                              <Check className="h-3.5 w-3.5 mr-1" /> Ativar
                            </Button>
                          )}
                          {m.status !== "blocked" && (
                            <Button size="sm" variant="outline" onClick={() => handleStatus(m.id, "blocked")} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8">
                              <X className="h-3.5 w-3.5 mr-1" /> Bloquear
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleRemove(m.id)} disabled={removing === m.id} className="border-white/10 text-white/70 hover:bg-red-500/10 hover:text-red-400 h-8">
                            {removing === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setExpanded((p) => ({ ...p, [m.id]: !isExpanded }))} className="text-white/70 hover:text-white">
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <Separator className="bg-white/10 mb-4" />
                    {m.is_master ? (
                      <div className="space-y-4">
                        <div className="rounded-lg border border-[#f5b022]/20 bg-[#f5b022]/5 p-4">
                          <p className="text-sm font-semibold text-admin-accent mb-1">Acesso total</p>
                          <p className="text-sm text-white/60">
                            Usuário MASTER tem acesso automático a todas as áreas do admin, inclusive novas páginas.
                          </p>
                        </div>
                        {m.user_id !== myUserId && (
                          <div className="flex justify-end">
                            <Button
                              variant="outline"
                              onClick={() => handleMaster(m.id, false)}
                              disabled={masterSaving === m.id}
                              className="border-white/10 text-white/70 hover:bg-white/10"
                            >
                              {masterSaving === m.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldOff className="h-4 w-4 mr-2" />}
                              Remover master
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-6">
                      {Object.entries(grouped).map(([group, routes]) => (
                        <div key={group}>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-bold tracking-widest uppercase text-admin-accent">{ADMIN_PAGE_GROUPS[group as AdminPageGroup] ?? group}</h3>
                            <button
                              onClick={() => toggleGroup(m.id, group as AdminPageGroup, routes)}
                              className="text-xs text-white/60 hover:text-white underline"
                            >
                              {routes.every((r) => pages.includes(r.route)) ? "Desmarcar grupo" : "Marcar grupo"}
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {routes.map((r) => (
                              <label
                                key={r.route}
                                className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 p-2.5 hover:bg-white/[0.07] cursor-pointer"
                              >
                                <Checkbox
                                  checked={pages.includes(r.route)}
                                  onCheckedChange={() => togglePage(m.id, r.route)}
                                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#f5b022] data-[state=checked]:text-[#13123a]"
                                />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-white truncate">{r.title}</p>
                                  <p className="text-xs text-white/50 truncate">{r.desc}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-wrap justify-end gap-2">
                        {m.user_id !== myUserId && (
                          <Button
                            variant="outline"
                            onClick={() => handleMaster(m.id, true)}
                            disabled={masterSaving === m.id}
                            className="border-[#f5b022]/30 text-admin-accent hover:bg-[#f5b022]/10"
                          >
                            {masterSaving === m.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                            Tornar master
                          </Button>
                        )}
                        <Button
                          onClick={() => handleSavePages(m.id)}
                          disabled={savingPages[m.id]}
                          className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a]"
                        >
                          {savingPages[m.id] ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                          Salvar permissões
                        </Button>
                      </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-[#13123a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-admin-accent" /> Convidar membro
            </DialogTitle>
            <DialogDescription className="text-white/60">Cria um usuário ativo e envia e-mail para definição de senha.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-white/80">Nome</Label>
              <Input value={inviteNome} onChange={(e) => setInviteNome(e.target.value)} required className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/80">E-mail</Label>
              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="space-y-3">
              <Label className="text-white/80">Páginas liberadas</Label>
              {Object.entries(grouped).map(([group, routes]) => (
                <div key={group}>
                  <h4 className="text-xs font-bold tracking-widest uppercase text-admin-accent mb-2">{ADMIN_PAGE_GROUPS[group as AdminPageGroup] ?? group}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {routes.map((r) => (
                      <label key={r.route} className="flex items-center gap-2 rounded border border-white/10 bg-white/5 p-2 cursor-pointer">
                        <Checkbox
                          checked={invitePages.includes(r.route)}
                          onCheckedChange={() => {
                            setInvitePages((prev) =>
                              prev.includes(r.route) ? prev.filter((x) => x !== r.route) : [...prev, r.route],
                            );
                          }}
                          className="border-white/30 data-[state=checked]:bg-[#f5b022] data-[state=checked]:text-[#13123a]"
                        />
                        <span className="text-sm text-white/90">{r.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} className="border-white/10 text-white hover:bg-white/10">
                Cancelar
              </Button>
              <Button type="submit" disabled={inviteLoading} className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a]">
                {inviteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Enviar convite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
